import type { ComponentProps } from 'react';
import { useCallback } from 'react';
import { useController } from 'react-hook-form';

import { Title, Container, ChipsContainer } from './styled';

import type { IOption } from '../../../../types/option';
import { Chip } from '../../../Chip';
import { Link } from '../../../Link';

type IChipSelectProps<TValue = string> = ComponentProps<typeof Container> & {
    title: string;
    inputName: string;
    options: Array<IOption<TValue>>;
};

export function ChipSelect<TValue = string>({
    title,
    inputName,
    options,
    ...props
}: IChipSelectProps<TValue>) {
    const {
        field: { value: fieldValues, onChange },
    } = useController({ name: inputName });
    const createHandler = (value: TValue) => () => {
        if (!fieldValues?.includes(value)) {
            onChange([...fieldValues, value]);
        } else {
            onChange(fieldValues.filter((item: TValue) => item !== value));
        }
    };

    const onDeselectAll = useCallback(() => {
        onChange(options.map(({ value }) => value));
    }, [onChange, options]);

    return (
        <Container {...props}>
            <Title>
                {title}
                <Link onClick={onDeselectAll}>Deselect all</Link>
            </Title>
            <ChipsContainer>
                {options.map(({ value, label }) => (
                    <Chip
                        key={JSON.stringify(value)}
                        checked={!fieldValues?.includes(value)}
                        onClick={createHandler(value)}
                    >
                        {label}
                    </Chip>
                ))}
            </ChipsContainer>
        </Container>
    );
}

ChipSelect.defaultProps = {};
